import { BBox } from "geojson";

import {
  FileRefinementType,
  GenerationType,
  GuidanceImageType,
  InferenceQuality,
} from "./schema.graphql";

export type JSONObject = { [key: string]: any };

export enum Permission {
  // basic
  basicWorkspace = "workspace:basic",
  // workspace settings
  AdminSettings = "workspace:settings",
  AdminBilling = "workspace:billing",
  AdminMembers = "workspace:members",
  DeleteWorkspace = "workspace:delete",
  // inference/training
  TrainStyle = "style:train",
  EditStyle = "style:edit",
  ImportStyle = "style:import",
  ExportStyle = "style:export",
  DownloadStyle = "style:download",
  UseStyle = "style:use",
  ViewStyleHistory = "style:viewHistory",
  UseInference = "inference:use",
  // drive
  CreateDirectory = "directory:create",
  EditDirectory = "directory:edit",
  DeleteDirectory = "directory:delete",
  // assets
  EditAsset = "asset:edit",
  ViewAsset = "asset:view",
}

export type TierConstraints = {
  briaStyles: boolean;
};

export type Rectangle = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type StyleCapabilities = {
  textToImageSizes: [number, number][];
  imageToImageSizes: [number, number][];
  customSizeStep?: number;
  customSizeTarget?: number;
  customSizeMin?: number;
  customSizeMax?: number;
  inpainting?: boolean;
  outpainting?: boolean;
  imageTypes: GuidanceImageType[];
  negativePrompt?: boolean;
  seed?: boolean;
  inferenceSteps?: boolean;
  quality?: boolean;
  sharpness?: boolean;
  guidanceScale?: boolean;
  imageGuidance?: boolean;
  upscaleRatios: number[];
};

export type InferenceStyleParameters = {
  id: string;
  weight: number;
};

export type GuidanceImageParameters = {
  url: string;
  type: GuidanceImageType;
  weight: number;
  preprocess?: boolean;
  selection?: boolean;
  openDropdownOnAttach?: boolean;
};

export type InferenceParameters = {
  generationType?: GenerationType;
  batchSize?: number;
  width?: number;
  height?: number;
  // styles
  styles?: InferenceStyleParameters[];
  // basic
  prompt?: string;
  seed?: number;
  numInferenceSteps?: number;
  guidanceScale?: number;
  quality?: InferenceQuality;
  sharpness?: number;
  // image guidance
  images?: GuidanceImageParameters[];
  // image to image / inpainting
  maskImageUrl?: string;
  maskForTransparency?: boolean;
  maskForNoneTransparency?: boolean;
  maskEdgeRadius?: number;
  // rotation
  rotationAngles?: number[];
  // postprocessing
  upscaleRatio?: number;
  upscaleDimensions?: number;
  upscaleImageGuidance?: number;
  // output
  returnMaskedAreaOnly?: boolean;
  maskBlendRadius?: number;
  returnBBox?: BBox;
  // canvas
  canvasFrameBBox?: BBox;
  canvasGenerationBBox?: BBox;
  // scheduler
  scheduler?: string;
  // internal
  unenhancedPrompt?: string;
};

export type InferenceFormDefaults = {
  initImageWeight: number;
  controlnetImageWeight: number;
};

export type InferenceForm = {
  parameters: InferenceParameters;
  defaults: InferenceFormDefaults;
  pixelDensity?: number;
  actualPixelDensity?: number;
};

export enum AssetLayerObjectType {
  Image = "IMAGE",
  Shape = "SHAPE",
}

export type AdjustmentFilter = {
  alpha: number;
  gamma: number;
  brightness: number;
  contrast: number;
  saturation: number;
  red: number;
  green: number;
  blue: number;
};

export type AssetLayerObject = {
  id: string;
  versionId: string;
  type: AssetLayerObjectType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  src?: string;
  props?: unknown;
  fileId?: string;
  styleInferenceId?: string;
  inferenceParameters?: InferenceParameters;
  srcFrame?: Rectangle;
  generationFrame?: Rectangle;
  alternativeSrcs?: string[];
  prevSrcs?: string[];
};

export type AssetLayer = {
  id: string;
  name: string;
  objects: AssetLayerObject[];
  drawable?: boolean;
  hidden?: boolean;
  adjustmentFilter: AdjustmentFilter;
};

export type AssetLayers = AssetLayer[];

export type AssetContents = {
  frame: BBox;
  layers: AssetLayers;
  activeLayerId?: string;
  inferenceForm: InferenceForm;
};

export type FileRefinementVersion = {
  id: string;
  type: FileRefinementType;
  url: string;
  width: number;
  height: number;
  sizeBytes: number;
};

export type FileRefinementVersions = {
  versions: FileRefinementVersion[];
  activeVersion: string;
};

export type NoticeInfo = {
  id: string;
  content: string;
  frequency: "ONCE" | "DAILY" | "WEEKLY" | "PER_LOGIN";
  updatedAt: string;
  isLive: boolean;
};

export type NoticesInfo = {
  notices: NoticeInfo[];
};
